<template>
  <div class="products-step step-inner-padded" data-testid="oc-products-step">
    <!-- This should never be shown, just for safety  -->
    <div v-if="loadingActiveBudget || budgetGateActive" class="col-12 text-center my-5">
      <IconLoader class="text-muted icon-rotate mb-3" :size="40" />
      <h2>{{ $t('orders.wizard.products.loading_budget') }}</h2>
      <p class="text-muted">{{ $t('orders.wizard.products.loading_budget.description') }}</p>
    </div>
    <div
      class="row cards-container g-3"
      v-bind="$attrs"
      data-testid="oc-products-selection-section"
      v-else-if="orderProducts.length">
      <div
        class="col-xxl-3 col-xl-4 col-6"
        v-for="(product, index) in orderProducts"
        :key="product.id"
        :data-product-id="product.id">
        <SingleProductCard
          :product="product"
          :isDisabled="selectionExcludedIds.includes(product.id)"
          :isRecommended="product.id in recommendations"
          :recommendationReason="recommendations[product.id]"
          @select="setSelectedProduct"
          class="animate__animated animate__fadeInRight animate__faster"
          :style="{ animationDelay: `${index * 0.1}s` }" />
      </div>
    </div>
    <div v-else class="col-12 text-center my-5">
      <IconBan class="mb-3" :size="40" />
      <h2>{{ $t('orders.wizard.products.no_products') }}</h2>
      <p class="text-muted">{{ $t('orders.wizard.products.no_products.description') }}</p>
    </div>
    <ProductSelectionModal
      v-model="selectedProduct"
      @saveProduct="handleSaveProduct"
      :remainingBudgetFn="activeBudgetStore.getRemainingBudgetAfterReserved" />
    <ProductCartDrawer
      v-model="openCart"
      @onProductsValidated="orderCreationStore.onProductsValidated"
      :isProductsStep="currentStep.key === 'products'" />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';

import { PJAS_DATE_FORMAT, formatDateTime } from '@/filters/dateFormat';
import type { IProduct, IProductToSave } from '@/types/product';
import SingleProductCard from '@/components/orders/product/ProductCard.vue';
import ProductSelectionModal from '@/components/orders/product/ProductBudgetSliderModal.vue';
import { useOrderCreationStore } from '@/stores/orderCreation';
import { useActiveBudgetStore } from '@/stores/activeBudget';
import { useCustomerFeaturesStore } from '@/stores/customerFeatures';
import { storeToRefs } from 'pinia';
import { IconBan, IconLoader } from '@tabler/icons-vue';
import ProductCartDrawer from '@/components/orders/product/ProductCartDrawer.vue';
import { calculateProductPrice } from '@/components/orders/product/cartCalculations';
import { useAutopilotContext } from '@/composables/useAutopilotContext';
import { useAutopilotFunctions } from '@/composables/useAutopilotFunctions';
import { useAutopilotRouteReady } from '@/composables/useAutopilotRouteReady';
import { useAutopilotWizardStep } from '@/composables/useAutopilotWizardStep';
import translator from '@/locale/translator';
import { formatCompactList } from '@/autopilot/formatCompactList';
import { toCartRow, toCatalogRow } from '@/autopilot/functions/orders/productCatalog';
import {
  createOpenProductFunction,
  createRecommendProductsFunction,
  type RecommendationHighlights,
} from '@/autopilot/functions/orders/bookingProducts';
import { defaultCurrency } from '@/config/app';

const pjasDateFormat = PJAS_DATE_FORMAT;

const allowNext = defineModel<boolean>('allowNext');

const selectedProduct = ref<IProduct | undefined>();

const activeBudgetStore = useActiveBudgetStore();
const orderCreationStore = useOrderCreationStore();
const featureStore = useCustomerFeaturesStore();
const { budgetGateActive, loadingActiveBudget, remainingBudget, activeBudget } = storeToRefs(activeBudgetStore);
const { orderProducts, selectedProducts, productsToSave, currentStep, openCart } = storeToRefs(orderCreationStore);

const recommendations = ref<RecommendationHighlights>({});
const routeReady = useAutopilotRouteReady();

onMounted(() => {
  const hasProductSelection = featureStore.featureOrderManagment?.provider_selection_mode === 'product';
  if (hasProductSelection && !orderProducts.value.length) {
    orderCreationStore
      .getOrderProducts()
      .catch(() => console.error('Failed to fetch products'))
      .finally(() => routeReady());
    return;
  }
  routeReady();
});

/**
 * An array of product ids that should be excluded from the selection
 */
const selectionExcludedIds = computed<number[]>(() => {
  // Disable products that are already selected
  let excludedIds: number[] = productsToSave.value.map((p) => p.id);

  // Exclude all products that have excludes_all set, because they exclude other products
  if (excludedIds.length > 0) {
    const excludesAllProductsIds = orderProducts.value.filter((p) => p.excludes_all).map((p) => p.id);
    excludedIds = [...excludedIds, ...excludesAllProductsIds];
  }

  for (const product of selectedProducts.value || []) {
    // if one product excludes all others
    // we iterate all products except the current one and
    // add them to our excludes
    if (product.excludes_all) {
      excludedIds = [];
      for (const otherProduct of orderProducts.value) excludedIds.push(otherProduct.id);

      return excludedIds;
    }

    // Exclude all other products that have this product type in exclude_types or in the excludes array
    const excludedByProductTypes = orderProducts.value
      .filter(
        (p) =>
          p.exclude_types.includes(product.type) ||
          p.excludes.includes(product.id) ||
          product.exclude_types.includes(p.type)
      )
      .map((p) => p.id);

    // if the product excludes specific ids, we add them to the excludes
    excludedIds = [...excludedIds, ...excludedByProductTypes, ...(product.excludes || [])];
  }

  // Remove duplicates
  return [...new Set([...excludedIds])];
});

/* -------------------------------- AUTOPILOT ------------------------------- */
useAutopilotWizardStep(() => ({
  key: 'products',
  title: translator.get('orders.wizard.products.title'),
  description:
    'The user books advertising products for the order. On screen: context.product_catalog, context.product_cart and context.booking_budget; follow context.booking_workflow.',
}));

useAutopilotContext('product_catalog', () => {
  const excludedIds = selectionExcludedIds.value;
  const cartIds = productsToSave.value.map((product) => product.id);
  return {
    description:
      'Products the user can book on this order. Drive recommendations with recommend_products; open one with open_product. Catalog price is a display string (e.g. "199 CHF").',
    items: formatCompactList(orderProducts.value.map((product) => toCatalogRow(product, { excludedIds, cartIds }))),
    recommended_ids: Object.keys(recommendations.value).map(Number),
  };
});

useAutopilotContext('product_cart', () => ({
  description: 'Products currently in the booking cart, not yet confirmed.',
  count: productsToSave.value.length,
  items: formatCompactList(productsToSave.value.map(toCartRow)),
}));

useAutopilotContext('booking_budget', () => ({
  description:
    'Remaining customer budget for this booking. remaining_cents is integer cents (null if the customer is not budget-bound).',
  gate_active: budgetGateActive.value,
  remaining_cents: remainingBudget.value?.max_available ?? null,
  currency: activeBudget.value?.currency || defaultCurrency,
}));

useAutopilotFunctions([
  createRecommendProductsFunction({
    getCatalog: () => orderProducts.value,
    getExcludedIds: () => selectionExcludedIds.value,
    setHighlights: (highlights) => {
      recommendations.value = highlights;
    },
    scrollTo: (productId) => {
      document
        .querySelector(`[data-product-id="${productId}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    },
  }),
  createOpenProductFunction({
    getCatalog: () => orderProducts.value,
    getExcludedIds: () => selectionExcludedIds.value,
    open: (product) => {
      selectedProduct.value = product;
    },
  }),
]);

function setSelectedProduct(product: any) {
  selectedProduct.value = product;
}

function handleSaveProduct(product: IProduct, productToSave: Partial<IProductToSave>) {
  let currency: string | undefined;
  if (product.type === 'single') {
    currency = product.price_currency;
  } else if (product.type === 'variable') {
    currency = product.budget_currency;
  } else if (product.type === 'target') {
    currency = product.spending_limit_currency;
  }

  const productToSaveData = {
    id: product.id,
    name: product.name,
    image: product.image?.data?.proxy.url_preview,
    startdate: productToSave.startdate && formatDateTime(productToSave.startdate, pjasDateFormat),
    enddate: productToSave.enddate && formatDateTime(productToSave.enddate, pjasDateFormat),
    budget: productToSave.budget,
    metric_goal: productToSave.metric_goal,
    spending_limit: productToSave.spending_limit,
    selectedOptions: productToSave.selectedOptions,
    vBudget: productToSave.vBudget,
    currency: currency,
  } as any;

  // keep budget as the campaign budget only; store the budget+options total separately so the
  // cart derives base+options itself (no double-count) while the vBudget reservation uses the total
  const priceInfo = calculateProductPrice(productToSaveData, orderProducts.value);
  productToSaveData.totalPrice = priceInfo.totalPrice;

  productsToSave.value.push(productToSaveData);

  activeBudgetStore.changeReservedVBudget(productToSaveData);
}

/* ---------------------------------- WATCH --------------------------------- */
watch(
  () => productsToSave.value.length,
  async (count) => {
    allowNext.value = count > 0;
  },
  { immediate: true }
);
</script>

<style scoped>
.list-move,
.list-enter-active,
.list-leave-active {
  transition: all 0.5s ease-in-out;
}

.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: translateY(-30px);
}

.list-leave-active {
  position: absolute;
}

.cards-container {
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
}

.cards-container > div .card {
  cursor: pointer;
}

.product-content {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
}

.product-content .product-purchase {
  display: flex;
  flex-direction: column;
  align-items: end;
}
</style>
